import { Field, useFormikContext } from 'formik';
import type { FC } from 'react';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import useFocusOnRouteChange from '../../hooks/useFocusOnRouteChange';
import { IAppState } from '../../store/types';
import { WizardStepLayout } from '../wizard/wizard-summary-layout';
import Label from '../label/label';
import Description from '../description/Description';

interface DetailStepProps {
    onNext: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    setStepDescriptions: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

const DetailStep: FC<DetailStepProps> = ({ onNext, setStepDescriptions }) => {
    const { translate } = useLocale();
    const { errors, values, setFieldValue } = useFormikContext<any>();
    const inputRef = useRef<HTMLInputElement>(null);
    const globalSite = useSelector((state: IAppState) => state.site.site.label);
    const globalSiteId = useSelector((state: IAppState) => state.site.site.value);

    useFocusOnRouteChange(inputRef);

    const onLocalNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setStepDescriptions(prev => ({ ...prev, step1: values.name || '' }));
        setFieldValue('siteId', globalSiteId);
        setFieldValue('siteName', globalSite);
        onNext(e);
    }

    return <WizardStepLayout onNext={onLocalNext} nextDisabled={!values.name}>
        <div className='flex flex-col space-y-1'>
            <span className={`text-portal-blue-primary-shade2 text-xl font-semibold`} >
                {translate.byKey(TranslationKeys.describeThisLift)}
            </span>
            {/* Name */}
            <div className='flex flex-col'>
                <div className="flex flex-row justify-between" id='nameLabel'>
                    <Label
                        htmlFor="name"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.name)}:</Label>
                    {errors.name ? <span className='text-red-600 text-xs'>{errors.name}</span> : null}
                </div>

                <div className="flex flex-row justify-between">
                    <Field
                        type="text"
                        name="name"
                        data-cy="nameField"
                        id="nameInput"
                        innerRef={inputRef}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input" />
                </div>
            </div>
            {/* Site */}
            <div className='flex flex-col'>
                <div className="flex flex-row justify-between" id="siteLabel">
                    <Label
                        htmlFor="siteId"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.site)}:</Label>
                    {errors.siteId ? <span className='text-red-600 text-xs'>{errors.siteId}</span> : null}
                </div>
                <Field
                    value={globalSite}
                    data-cy="siteField"
                    disabled={true}
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
            {/* Description */}
            <div className='flex flex-col'>
                <div className="flex flex-row justify-between" id="descriptionLabel">
                    <Label
                        htmlFor="description"
                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.description)}:</Label>
                </div>
                <Description title={''} height='h-24' remainingCharactersClass='text-gray-800 text-xs' modalDescription={false} />
            </div>
        </div>
    </WizardStepLayout>
}

export default DetailStep;